import { useEffect, useRef, useState } from 'react';
import { Send, Sparkles } from 'lucide-react';
import { tasksAPI } from '../services/api.js';

// Chat task-intake for the exec-admin. The owner types a task; the AI confirms,
// asks for anything missing (e.g. a number not in the directory), and places the
// call via the callout engine. A dashboard alternative to phoning the assistant.
// onTasksCreated() lets the parent refresh its task buckets when a call is placed.

const GREETING = {
  role: 'assistant',
  content: "Hi — tell me what you need and I'll take care of it. For example: “Call John and let him know I'm running ten minutes late.”"
};

export default function AssistantChat({ onTasksCreated }) {
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notReady, setNotReady] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setError('');
    // Only real user/assistant turns go to the API (drop the local greeting).
    const history = messages.filter((m) => m !== GREETING);
    const next = [...messages, { role: 'user', content: text }];
    setMessages(next);
    setInput('');
    setBusy(true);
    try {
      const convo = [...history, { role: 'user', content: text }];
      const r = await tasksAPI.chat(convo);
      setMessages((m) => [...m, { role: 'assistant', content: r?.reply || 'Got it.' }]);
      if (r?.tasks?.length) onTasksCreated?.();
    } catch (err) {
      // 404 = route not deployed yet; 503 = flagged off; 403 = not enabled for this
      // account — all mean "not available yet", so degrade gracefully rather than
      // showing a raw error.
      if ([404, 503, 403].includes(err.status)) {
        setNotReady(true);
        setMessages((m) => m.slice(0, -1)); // drop the just-added user msg
        setInput(text);
      } else {
        setError(err.body?.message || err.message || 'The assistant could not respond.');
      }
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <section className="card">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={18} className="text-alfred-gold" />
        <h2 className="text-lg text-stone-100">Ask your assistant</h2>
      </div>

      {notReady ? (
        <p className="rounded-lg border border-amber-800/50 bg-amber-950/30 px-4 py-2 text-sm text-amber-200">
          Chat tasking is being set up — it will be available shortly. In the meantime, just call your line.
        </p>
      ) : (
        <>
          <div ref={scrollRef} className="max-h-80 overflow-y-auto space-y-3 mb-3 pr-1">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === 'user'
                    ? 'bg-alfred-gold/20 text-stone-100 border border-alfred-gold/30'
                    : 'bg-stone-900/60 text-stone-200 border border-stone-800'
                }`}>
                  {m.content}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="rounded-2xl px-3 py-2 text-sm bg-stone-900/60 text-stone-500 border border-stone-800">…</div>
              </div>
            )}
          </div>

          {error && <p className="mb-2 text-sm text-red-300">{error}</p>}

          <div className="flex items-end gap-2">
            <textarea
              className="field-input min-h-[44px] max-h-32 resize-none"
              placeholder="Type a task — e.g. “Call my accountant and ask for the Q3 numbers.”"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              rows={1}
            />
            <button type="button" className="btn-primary shrink-0" onClick={send} disabled={busy || !input.trim()}>
              <Send size={14} /> Send
            </button>
          </div>
        </>
      )}
    </section>
  );
}
